import {GenericObject} from "@/hooks/useForm";
import React, {useEffect, useMemo, useState} from "react";
import ValidatedInput from "@common/ValidatedInput";
import DropdownMultiGroupSelect from "patient-ping-remedy/packages/dropdown-multi-group";
import {EVENT_TYPES} from "@/components/pages/settings/event_consumers/EventConsumers.constants";
import {nonGroupedValues} from "@common/FilterComponent";
import {EventConsumersContainer} from "@/components/pages/settings/event_consumers/EventConsumerForm.styles";
import EventDetailFiltersForm from "@/components/pages/settings/event_consumers/EventDetailFiltersForm";
import {EventConsumer} from "@/api/dto/dto";
import {state_codes} from "@/helpers/state_codes";
import Input from "patient-ping-remedy/packages/input";
import Spacer from "patient-ping-remedy/packages/spacer";
import {sizes} from "patient-ping-remedy/packages/theme";
import InfoIconTooltip from "@common/InfoIconTooltip";
import {validateIntegerList} from "@/components/pages/settings/event_consumers/EventConsumers.utils";
import Helpers from "@/helpers/helpers";

type Props = {
    form: GenericObject,
    handleChange: (event: (React.ChangeEvent | null), name: string, value: any) => void,
    isWithinModal?: boolean,
    eventConsumer?: EventConsumer | undefined
}

const EventConsumerForm = (props: Props) => {
  const [facilityIds, setFacilityIds] = useState<string>(
    props.form?.filters?.value?.facilityIds?.values?.join(", ")
  );

  const eventStateOptions: nonGroupedValues[] = useMemo(() => {
    return state_codes.map(code => {
      return {
        id: code.value,
        value: code.value,
        label: code.value
      };
    });
  }, [state_codes]);

  useEffect(() => {
    if (Helpers.isDefined(facilityIds)
      && Helpers.isEmpty(validateIntegerList(
        facilityIds, "Facility IDs cannot be empty.", "Facility IDs must be integers."
      ))) {
      const parsed = facilityIds
        .split(",")
        .map(id => id.trim())
        .filter(id => id && !isNaN(Number(id)))
        .map(Number);
      const updated = {
        ...props.form.filters.value,
        facilityIds: parsed.length > 0 ? { values: parsed } : null
      };
      props.handleChange(null, 'filters', updated);
    }
  }, [facilityIds]);

  return (
    <EventConsumersContainer isWithinModal={props.isWithinModal ?? false}>
      <ValidatedInput
        key={"name"}
        id={"name"}
        labelText={"Name"}
        required={"required"}
        value={props.form?.name?.value ?? ''}
        onChange={(e) => props.handleChange(e, 'name', e.target.value)}
        validate={() => props.form?.name?.valid.valid ? '' : props.form?.name?.valid.message}
      />

      {!props.isWithinModal &&
      <Input
        value={props.eventConsumer?.consumerUuid ?? ''}
        disabled={true}
        required={"required"}
        labelText={"Target UUID"}
      />
      }

      <DropdownMultiGroupSelect
        id={"event-types"}
        label={"Event types"}
        options={EVENT_TYPES}
        value={EVENT_TYPES.filter(eventTypes =>
          props.form?.filters?.value?.eventTypes?.values?.includes(eventTypes.value))}
        handleChange={(event) => {
          const values = {
            ...props.form?.filters?.value,
            eventTypes: {
              values: event?.map((item) => item.value)
            },
            eventDetailFilters: (event ?? []).reduce((acc, item) => {
              const key = item.value;
              const value = props.form?.filters?.value?.eventDetailFilters?.[key];
              if (value !== undefined) {
                acc[key] = value;
              }
              return acc;
            }, {} as Record<string, any>)
          };
          props.handleChange(null, 'filters', values);
        }}
        isRequiredField={true}
        isWithinModal={props.isWithinModal}
        error={props.form?.filters?.value?.eventTypes?.values?.length === 0}
      />
      <DropdownMultiGroupSelect
        id={"event-states"}
        label={"Event states"}
        options={eventStateOptions}
        value={eventStateOptions.filter(eventTypes =>
          props.form?.filters?.value?.eventStates?.values?.includes(eventTypes.value))}
        handleChange={(event) => {
          const values = {...props.form?.filters?.value, eventStates: {values: event?.map((item) => item.value)}};
          props.handleChange(null, 'filters', values);
        }}
        isRequiredField={true}
        isWithinModal={props.isWithinModal}
        error={props.form?.filters?.value?.eventStates?.values?.length === 0}
      />

      <ValidatedInput
        id={"facility-ids"}
        //@ts-expect-error expects string
        labelText={
          <span>Facility IDs
            <Spacer itemWidth={sizes.xSmall}/>
            <InfoIconTooltip
              text={"Enter facility IDs, separated by commas. Only numeric values are allowed."}
            />
          </span>
        }
        value={facilityIds ?? props.form?.filters?.value?.facilityIds?.values?.join(", ")}
        onChange={(event) => {setFacilityIds(event.target.value);}}
        validate={(val) => validateIntegerList(
          val, "Facility IDs cannot be empty.", "Facility IDs must be integers."
        )}
      />

      {
        EVENT_TYPES
          .filter(eventType => props.form?.filters?.value?.eventTypes?.values?.includes(eventType.value))
          .map((eventType) =>
            <EventDetailFiltersForm
              key={eventType.value}
              form={props.form}
              handleChange={props.handleChange}
              eventType={eventType}
              isWithinModal={props.isWithinModal}
            />
          )
      }
    </EventConsumersContainer>
  );
};

export default React.memo(EventConsumerForm);
